/**
 * Netcore Smartech SDK utility functions
 * These functions wrap the Netcore SDK calls and can be used both from web and native app
 */

declare global {
  interface Window {
    smartech?: (action: string, ...args: any[]) => void;
    SmartechBaseReact?: {
      updateUserProfile: (userProfile: NetcoreUserProfile) => void;
      setUserIdentity: (userIdentity: string) => void;
      login: (userId: string) => void;
      trackEvent: (eventName: string, eventPayload?: Record<string, any>) => void;
    };
  }
}

export interface NetcoreUserProfile {
  FIRST_NAME?: string;
  LAST_NAME?: string;
  EMAIL?: string;
  MOBILE?: string;
  AGE?: number;
  GENDER?: string;
  COUNTRY?: string;
  STATE?: string;
  CITY?: string;
  PINCODE?: string;
  BIRTH_DATE?: string; // Format: yyyy-mm-dd
  CPF?: string;
  [key: string]: string | number | undefined;
}

/**
 * Check if Netcore SDK is available
 */
export const isNetcoreAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.smartech === 'function';
};

/**
 * Update user profile in Netcore
 * This creates or updates the user in Netcore database
 */
export const updateUserProfile = (userProfile: NetcoreUserProfile): void => {
  if (!isNetcoreAvailable()) {
    return;
  }

  try {
    window.smartech!('updateUserProfile', userProfile);
  } catch (error) {
    // Silent fail
  }
};

/**
 * Set user identity in Netcore
 * This identifies the current user with a unique identifier (CPF/document)
 * @param userIdentity - The user's CPF (document) as the identity
 */
export const setUserIdentity = (userIdentity: string): void => {
  if (!isNetcoreAvailable() || !userIdentity) {
    return;
  }

  try {
    window.smartech!('setUserIdentity', userIdentity);
  } catch (error) {
    // Silent fail
  }
};

/**
 * Login user in Netcore
 * This logs the user into the Netcore system
 * @param userIdentity - The user's CPF (document) as the identity
 */
export const login = (userIdentity: string): void => {
  if (!isNetcoreAvailable() || !userIdentity) {
    return;
  }

  try {
    window.smartech!('login', userIdentity);
  } catch (error) {
    // Silent fail
  }
};

/**
 * Track event in Netcore
 */
export const trackEvent = (
  eventName: string,
  eventPayload?: Record<string, any>,
): void => {
  if (!isNetcoreAvailable() || !eventName) {
    return;
  }

  try {
    window.smartech!('track', eventName, eventPayload || {});
  } catch (error) {
    // Silent fail
  }
};

/**
 * Convert User object to NetcoreUserProfile format
 */
export const userToNetcoreProfile = (user: {
  id?: number | string;
  first_name?: string;
  last_name?: string;
  name?: string;
  email?: string;
  cellphone?: string;
  phone?: string;
  gender?: string;
  birth_date?: string;
  cpf?: string;
  [key: string]: any;
}): NetcoreUserProfile => {
  const profile: NetcoreUserProfile = {};

  if (user.first_name) {
    profile.FIRST_NAME = user.first_name;
  }
  if (user.last_name) {
    profile.LAST_NAME = user.last_name;
  }
  if (user.email) {
    profile.EMAIL = user.email;
  }
  if (user.cellphone) {
    profile.MOBILE = user.cellphone;
  } else if (user.phone) {
    profile.MOBILE = user.phone;
  }
  if (user.gender) {
    profile.GENDER = user.gender;
  }
  if (user.birth_date) {
    // Convert date format to yyyy-mm-dd if needed
    const dateStr = user.birth_date;
    if (dateStr.includes('/')) {
      // Format: dd/mm/yyyy -> yyyy-mm-dd
      const [day, month, year] = dateStr.split('/');
      profile.BIRTH_DATE = `${year}-${month}-${day}`;
    } else {
      profile.BIRTH_DATE = dateStr;
    }
  }
  if (user.cpf) {
    profile.CPF = user.cpf;
  }

  return profile;
};

/**
 * Send message to native app via ReactNativeWebView.postMessage
 * Follows the same pattern as Google/Apple login
 */
export const sendNetcoreMessageToNative = (
  type: string,
  payload?: Record<string, any>,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if ((window as any).ReactNativeWebView?.postMessage) {
    try {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({
          type,
          ...payload,
        }),
      );
    } catch (error) {
      // Silent fail
    }
  }
};

/**
 * Initialize SmartechBaseReact interface for native app communication
 * This exposes functions that the native app can call
 */
export const initializeSmartechBaseReact = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.SmartechBaseReact = {
    updateUserProfile: (userProfile: NetcoreUserProfile) => {
      updateUserProfile(userProfile);
    },
    setUserIdentity: (userIdentity: string) => {
      setUserIdentity(userIdentity);
    },
    login: (userId: string) => {
      login(userId);
    },
    trackEvent: (eventName: string, eventPayload?: Record<string, any>) => {
      trackEvent(eventName, eventPayload);
    },
  };
};

